import { Check, ImageOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb } from '@/features/media';

import { useVariantImage } from '../hooks/use-variant-image';

export interface VariantImageDialogProps {
  product: Product;
  variant: Variant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Picks the variant's image from the product's own media, as Shopify does: one library per
 * product, an image can show several variants. Saving rewrites the gallery's variant links.
 */
export function VariantImageDialog({
  product,
  variant,
  open,
  onOpenChange,
}: VariantImageDialogProps) {
  const { t } = useTranslation(['products', 'common']);
  const message = useErrorMessage();
  const { items, current, pick, isLoading, save } = useVariantImage(product, variant);
  const [choice, setChoice] = useState<string | null>(current);

  const apply = () => pick(choice, () => onOpenChange(false));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('products:variantPage.image.title')}</DialogTitle>
          <DialogDescription>{t('products:variantPage.image.description')}</DialogDescription>
        </DialogHeader>
        {items.length === 0 && !isLoading ? (
          <p className="text-muted-foreground text-sm">{t('products:variantPage.image.empty')}</p>
        ) : (
          <div role="radiogroup" className="grid grid-cols-4 gap-2 sm:grid-cols-5">
            <button
              type="button"
              role="radio"
              aria-checked={choice === null}
              aria-label={t('products:variantPage.image.none')}
              onClick={() => setChoice(null)}
              className={cn(
                'text-muted-foreground flex aspect-square flex-col items-center justify-center gap-1 rounded-md border text-xs',
                choice === null && 'ring-primary ring-2',
              )}
            >
              <ImageOff aria-hidden className="size-5" />
              {t('products:variantPage.image.none')}
            </button>
            {items.map((item) => (
              <button
                key={item.mediaId}
                type="button"
                role="radio"
                aria-checked={choice === item.mediaId}
                aria-label={item.media.alt ?? item.media.filename ?? item.mediaId}
                onClick={() => setChoice(item.mediaId)}
                className={cn(
                  'relative aspect-square overflow-hidden rounded-md border',
                  choice === item.mediaId && 'ring-primary ring-2',
                )}
              >
                <MediaThumb media={item.media} className="size-full" />
                {choice === item.mediaId ? (
                  <span className="bg-primary text-primary-foreground absolute top-1 right-1 rounded-full p-0.5">
                    <Check aria-hidden className="size-3" />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}
        {save.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(save.error)}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={choice === current || save.isPending || items.length === 0}
            onClick={apply}
          >
            {save.isPending ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
